/**
 * KpiTile — one read-only metric tile: the formatted value (LTR mono island), the metric label, the
 * sample count, and the rapport tone band rendered as color + glyph + text (never color alone —
 * daltonisme + médico-légal). Pure presentation; the screen resolves the meta + tone.
 */
import { useT } from "@/app";
import { formatMetric, toneFor, TONE_COLOR, TONE_GLYPH, type MetricMeta } from "../lib";
import type { LiveMetric } from "../types";

export function KpiTile({ metric, meta }: { metric: LiveMetric; meta: MetricMeta }) {
  const { t } = useT();
  const tone = toneFor(meta, metric.metric_value);
  const informational = meta.good == null || meta.watch == null;
  const toneColor = TONE_COLOR[tone];
  const label = t(`analytics.metric.${meta.key}`);
  const unitKey = `analytics.metric.${meta.key}.unit`;
  const unit = t(unitKey);
  const hasUnit = unit !== unitKey;

  return (
    <div
      role="group"
      aria-label={label}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-2)",
        padding: "var(--nv-s-4)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-surface)",
        border: "1px solid var(--nv-line-faint)",
        borderInlineStart: `3px solid ${toneColor}`,
        boxShadow: "var(--nv-e1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--nv-s-2)" }}>
        <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>{label}</span>
        {!informational && <ToneChip tone={tone} />}
      </div>

      <span
        className="nv-mono nv-ltr-island"
        style={{ fontSize: "var(--nv-fs-h1)", fontWeight: 600, color: "var(--nv-ink)", lineHeight: 1.1 }}
      >
        {formatMetric(metric.metric_value, meta.format)}
        {hasUnit && (
          <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)", marginInlineStart: "var(--nv-s-1)" }}>
            {unit}
          </span>
        )}
      </span>

      {metric.count > 0 && (
        <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>
          <span className="nv-ltr-island">{t("analytics.metric.count.suffix", { n: metric.count })}</span>
        </span>
      )}
    </div>
  );
}

/** The tone band chip — color + mandated glyph + text. */
export function ToneChip({ tone }: { tone: "good" | "watch" | "alert" }) {
  const { t } = useT();
  return (
    <span
      className="nv-badge"
      style={{ color: TONE_COLOR[tone], background: "var(--nv-surface-2)" }}
    >
      <span className="nv-badge__glyph" aria-hidden>
        {TONE_GLYPH[tone]}
      </span>
      {t(`analytics.tone.${tone}`)}
    </span>
  );
}
